import {
  AboutField,
  ArmApiClient,
  armApiRef,
  checkAndClearExpiredCache,
  getCachedGroups,
  setCachedGroups,
  useGetAzureToken,
} from '@internal/plugin-azure-resources-common-react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAsync, useAsyncFn } from 'react-use';

import {
  IconLinkVerticalProps,
  InfoCardVariants,
} from '@backstage/core-components';
import { useApi } from '@backstage/core-plugin-api';
import {
  catalogApiRef,
  EntityRefLink,
  useEntity,
} from '@backstage/plugin-catalog-react';
import { JsonValue } from '@backstage/types';

import { capitalize } from '@material-ui/core';
import Button from '@material-ui/core/Button';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import CardHeader from '@material-ui/core/CardHeader';
import Chip from '@material-ui/core/Chip';
import Divider from '@material-ui/core/Divider';
import Grid from '@material-ui/core/Grid';
import IconButton from '@material-ui/core/IconButton';
import MuiLink from '@material-ui/core/Link';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import DeleteIcon from '@material-ui/icons/Delete';
import EditIcon from '@material-ui/icons/Edit';
import GetAppIcon from '@material-ui/icons/GetApp';

import { kaapApiRef } from '../../api';
import {
  useAzureToken,
  useNavigateToTemplate,
  useUserSecurityGroupIds,
} from '../../hooks';
import { Tenant } from '../../types';
import { getLink } from '../../utils';
import { buildFormData } from '../../utils/formDataBuilder';
import { isTenantManagedBy } from '../../utils/tenantAccess';
import { LinkIcon } from '../LinkIcon';

const useStyles = makeStyles(theme => ({
  gridItemCard: {
    display: 'flex',
    flexDirection: 'column',
    height: 'calc(100% - 10px)',
    marginBottom: '10px',
  },
  fullHeightCard: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  gridItemCardContent: {
    flex: 1,
  },
  fullHeightCardContent: {
    flex: 1,
  },
  cardContent: {
    padding: theme.spacing(1.5),
    '&:last-child': {
      paddingBottom: theme.spacing(1.5),
    },
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  chipList: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '6px',
  },
  description: {
    wordBreak: 'break-word',
  },
  actionBlock: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    flexWrap: 'wrap',
  },
  actionBlockText: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
    flex: 1,
    minWidth: 0,
  },
  commandText: {
    wordBreak: 'break-word',
  },
  sectionDivider: {
    marginTop: theme.spacing(0.25),
    marginBottom: theme.spacing(0.25),
  },
  subheaderLinks: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: theme.spacing(2),
    marginTop: 15,
    paddingTop: theme.spacing(1),
    marginBottom: 15,
  },
  subheaderLink: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
    color: theme.palette.text.secondary,
    whiteSpace: 'nowrap',
    fontSize: theme.typography.body2.fontSize,
    lineHeight: 1.2,
  },
  subheaderLinkDisabled: {
    opacity: 0.45,
    textDecoration: 'none',
    cursor: 'default',
  },
  subheaderIcon: {
    display: 'inline-flex',
    alignItems: 'center',
    lineHeight: 0,
    flexShrink: 0,
    '& img, & svg': {
      width: 18,
      height: 18,
    },
  },
  subheaderMenuButton: {
    minWidth: 'auto',
    padding: 0,
    textTransform: 'none',
    justifyContent: 'flex-start',
    color: theme.palette.text.secondary,
    fontSize: theme.typography.body2.fontSize,
    lineHeight: 1.2,
    '& .MuiButton-startIcon': {
      marginRight: theme.spacing(0.75),
    },
    '& .MuiButton-endIcon': {
      marginLeft: theme.spacing(0.25),
    },
    '& .MuiButton-startIcon img, & .MuiButton-startIcon svg': {
      width: 18,
      height: 18,
    },
  },
  menuItemIcon: {
    minWidth: theme.spacing(4),
    '& img, & svg': {
      width: 18,
      height: 18,
    },
  },
  menuItemText: {
    margin: 0,
    '& .MuiTypography-root': {
      fontSize: theme.typography.body2.fontSize,
    },
  },
}));

const downloadTextFile = (content: string, filename: string) => {
  const blob = new Blob([content], {
    type: 'application/x-yaml;charset=utf-8',
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

const getRepositoryLabel = (url: string): string => {
  const prefix = 'https://github.com/';
  const index = url.indexOf(prefix);

  return index === -1 ? url : url.substring(index + prefix.length);
};

const buildRepositoryHref = (repoUrl: string, rootPath?: string): string => {
  if (!rootPath) {
    return repoUrl;
  }

  return `${repoUrl}/tree/main/${rootPath}`;
};

const renderList = (values: string[], className: string) => {
  if (values.length === 0) {
    return undefined;
  }

  return (
    <div className={className}>
      {values.map(value => (
        <Typography key={value} variant="body2">
          {value}
        </Typography>
      ))}
    </div>
  );
};

const renderSecurityGroupLinks = (
  objectIds: string[],
  groupNames: string[],
  className: string,
) => {
  if (objectIds.length === 0) {
    return undefined;
  }

  return (
    <div className={className}>
      {objectIds.map((groupId, index) => (
        <MuiLink
          key={groupId}
          href={`https://portal.azure.com/#view/Microsoft_AAD_IAM/GroupDetailsMenuBlade/~/Overview/groupId/${groupId}`}
          target="_blank"
          rel="noopener noreferrer"
          variant="body2"
        >
          {groupNames[index] ?? groupId}
        </MuiLink>
      ))}
    </div>
  );
};

const renderRepositoryLinks = (
  repos: string[],
  className: string,
  rootPath?: string,
) => {
  if (repos.length === 0) {
    return undefined;
  }

  return (
    <div className={className}>
      {repos.map((repoUrl, index) => (
        <MuiLink
          key={`${repoUrl}-${index}`}
          href={index === 0 ? buildRepositoryHref(repoUrl, rootPath) : repoUrl}
          target="_blank"
          rel="noopener noreferrer"
          variant="body2"
        >
          {getRepositoryLabel(repoUrl)}
        </MuiLink>
      ))}
    </div>
  );
};

const toMemoryString = (value?: number | null): string | undefined => {
  if (value === undefined || value === null || Number.isNaN(Number(value))) {
    return undefined;
  }

  const mi = Number(value);
  if (mi >= 1024) {
    const gi = mi / 1024;
    // If gi is integer, no decimals; otherwise one decimal
    return `${gi % 1 === 0 ? String(gi) : gi.toFixed(1)}Gi`;
  }

  return `${mi}Mi`;
};

const getValues = (fetchedTenant?: Tenant, entitySpecTenant?: JsonValue) => {
  const entitySpecTenantObject = entitySpecTenant as any;
  return {
    tier: entitySpecTenantObject?.tier ?? fetchedTenant?.tier,
    stage: entitySpecTenantObject?.stage ?? fetchedTenant?.stage,
    kubeconfig: atob(fetchedTenant?.kubeConfig ?? ''),
    kubeconfigFileName: `kaap-${entitySpecTenantObject.name}.kubeconfig`,
    applicationName: fetchedTenant?.application?.name,
    applicationPU: fetchedTenant?.application?.ouname,
    applicationCC: fetchedTenant?.application?.ccname,
    clusterId: entitySpecTenantObject.clusterId,
    clusterName:
      entitySpecTenantObject.clusterId?.split('/')[1] ??
      fetchedTenant?.getClusterName() ??
      '',
    clusterSubscription:
      entitySpecTenantObject.clusterId?.split('/')[0] ??
      fetchedTenant?.clusterId?.split('/')[0] ??
      '',
    slackChannel:
      entitySpecTenantObject.slackChannel ?? fetchedTenant?.slackChannel,
    pimStatus: fetchedTenant?.pimEnabled ? 'Enabled' : 'Disabled',
    nodeGroups: entitySpecTenantObject.nodeGroups,
    securityObjectIds: entitySpecTenantObject.securityObjectIds ?? [],
    argocdProject: fetchedTenant?.argocdProject,
    rootProjectRepository: fetchedTenant?.argocdProject?.repositories.slice(
      0,
      1,
    ),
    additionalProjectRepositories:
      fetchedTenant?.argocdProject?.repositories.slice(1),
    helmRepositories: fetchedTenant?.argocdProject?.helmRepositories,
    namespaceQuota:
      entitySpecTenantObject?.namespaceQuota ?? fetchedTenant?.namespaceQuota,
    cpuRequest:
      (entitySpecTenantObject?.tenantResourceQuota?.cpuRequest ??
        fetchedTenant?.getCpuRequest()) / 1000,
    cpuLimit:
      (entitySpecTenantObject?.tenantResourceQuota?.cpuLimit ??
        fetchedTenant?.getCpuLimit()) / 1000,
    memoryRequest: toMemoryString(
      entitySpecTenantObject?.tenantResourceQuota?.memoryRequest ??
        fetchedTenant?.getMemoryRequest(),
    ),
    memoryLimit: toMemoryString(
      entitySpecTenantObject?.tenantResourceQuota?.memoryLimit ??
        fetchedTenant?.getMemoryLimit(),
    ),
    cabInform: entitySpecTenantObject?.cabInform ?? false,
  };
};

export interface KaaPTenantAboutCardProps {
  variant?: InfoCardVariants;
}

export const KaaPTenantAboutCard = (props: KaaPTenantAboutCardProps) => {
  const classes = useStyles();
  const { entity } = useEntity();
  const { authProvider } = useAzureToken();
  const armApi = useApi(armApiRef);
  const catalogApi = useApi(catalogApiRef);
  const owner = entity.spec?.owner as string;
  const { value: ownerGroup } = useAsync(async () => {
    if (!owner) {
      return undefined;
    }

    const result = await catalogApi.getEntities({
      filter: {
        kind: 'Group',
        'metadata.name': owner.toLowerCase(),
      },
    });

    const groupEntity = result.items[0];
    if (!groupEntity) {
      return undefined;
    }

    return {
      kind: groupEntity.kind,
      namespace: groupEntity.metadata.namespace ?? 'default',
      name: groupEntity.metadata.name,
      title: groupEntity.metadata.title || groupEntity.metadata.name || owner,
    };
  }, [catalogApi, owner]);
  const kaapApi = useApi(kaapApiRef);
  const token = useGetAzureToken();
  const { value: fetchedTenant } = useAsync(async () => {
    const tenantName = entity.metadata.name;
    if (!tenantName) return undefined;
    if (!token) return undefined;
    try {
      return await kaapApi.getTenant(tenantName, token.token);
    } catch (e) {
      return undefined;
    }
  }, [kaapApi, token, entity]);

  const entitySpecTenant = useMemo(() => {
    return entity.spec?.tenant;
  }, [entity]);

  const {
    tier,
    stage,
    kubeconfig,
    kubeconfigFileName,
    applicationName,
    applicationPU,
    applicationCC,
    clusterId,
    clusterName,
    clusterSubscription,
    pimStatus,
    nodeGroups,
    securityObjectIds,
    argocdProject,
    rootProjectRepository,
    additionalProjectRepositories,
    helmRepositories,
    namespaceQuota,
    cpuRequest,
    cpuLimit,
    memoryRequest,
    memoryLimit,
    cabInform,
  } = useMemo(
    () => getValues(fetchedTenant, entitySpecTenant),
    [fetchedTenant, entitySpecTenant],
  );

  const navigateToTemplate = useNavigateToTemplate();

  const { variant } = props;
  const [observabilityMenuAnchor, setObservabilityMenuAnchor] =
    useState<HTMLElement | null>(null);
  const { groupIds: userGroupIds } = useUserSecurityGroupIds();

  const hasAccess = useMemo(
    () => isTenantManagedBy(entity, userGroupIds),
    [entity, userGroupIds],
  );
  const [adGroupsResponse, getAdGroups] = useAsyncFn(
    async (groupIds: string[]) => {
      const graphClient = ArmApiClient.buildGraphClient(authProvider);
      checkAndClearExpiredCache();

      const groupMap = new Map<string, string>();
      const cachedGroups = getCachedGroups();
      const newGroups = new Map<string, string>();
      for (const groupId of groupIds) {
        if (groupMap.has(groupId)) {
          continue;
        }

        if (cachedGroups.has(groupId)) {
          groupMap.set(groupId, cachedGroups.get(groupId)!);
          continue;
        }

        try {
          const adGroup = await armApi.getGroup(groupId, graphClient);
          const groupName = adGroup.name;
          groupMap.set(groupId, groupName);
          newGroups.set(groupId, groupName);
        } catch {
          const fallbackName = groupId;
          groupMap.set(groupId, fallbackName);
        }
      }

      if (newGroups.size > 0) {
        setCachedGroups(newGroups);
      }

      return groupMap;
    },
  );

  const securityGroupNames = useMemo(
    () =>
      securityObjectIds.map(
        (securityObjectId: any) =>
          adGroupsResponse.value?.get(securityObjectId) ?? securityObjectId,
      ),
    [adGroupsResponse.value, securityObjectIds],
  );

  const buildEntityLink = useCallback(
    (name: string, label: string, iconName?: string): IconLinkVerticalProps => {
      const href = getLink(name, entity, fetchedTenant);

      return {
        label,
        disabled: !href || href === '#',
        icon: <LinkIcon name={iconName ?? name} />,
        href,
      };
    },
    [fetchedTenant, entity],
  );

  const subHeaderLinks: IconLinkVerticalProps[] = [
    ...(argocdProject ? [buildEntityLink('argo', 'Argo CD', 'argo')] : []),
    buildEntityLink('alerts', 'Alertmanager', 'prometheus'),
    buildEntityLink('vmAlert', 'VMAlert', 'prometheus'),
    buildEntityLink('costs', 'Costs', 'opencost'),
    buildEntityLink('slack', 'Slack', 'slack'),
  ];
  const observabilityLinks: IconLinkVerticalProps[] = [
    buildEntityLink('metrics', 'Metrics', 'grafana'),
    buildEntityLink('redMetrics', 'RED Metrics', 'grafana'),
    buildEntityLink('trivy', 'Vulnerabilities', 'grafana'),
    buildEntityLink('logs', 'Logs', 'otel'),
  ];
  const hasObservabilityLinks = observabilityLinks.some(link => !link.disabled);

  const openObservabilityMenu = (event: React.MouseEvent<HTMLElement>) => {
    if (!hasObservabilityLinks) {
      return;
    }

    setObservabilityMenuAnchor(event.currentTarget);
  };

  const closeObservabilityMenu = () => {
    setObservabilityMenuAnchor(null);
  };

  const subheader = (
    <div className={classes.subheaderLinks}>
      <Button
        size="small"
        className={classes.subheaderMenuButton}
        startIcon={<LinkIcon name="grafana" />}
        endIcon={<ArrowDropDownIcon fontSize="small" />}
        onClick={openObservabilityMenu}
        disabled={!hasObservabilityLinks}
      >
        Observability
      </Button>
      {subHeaderLinks.map(linkItem => {
        const content = (
          <>
            <span className={classes.subheaderIcon}>{linkItem.icon}</span>
            <span>{linkItem.label}</span>
          </>
        );

        if (linkItem.disabled || !linkItem.href || linkItem.href === '#') {
          return (
            <span
              key={linkItem.label}
              className={`${classes.subheaderLink} ${classes.subheaderLinkDisabled}`}
            >
              {content}
            </span>
          );
        }

        return (
          <MuiLink
            key={linkItem.label}
            href={linkItem.href}
            target="_blank"
            rel="noopener noreferrer"
            underline="none"
            className={classes.subheaderLink}
          >
            {content}
          </MuiLink>
        );
      })}
      <Menu
        anchorEl={observabilityMenuAnchor}
        keepMounted
        open={Boolean(observabilityMenuAnchor)}
        onClose={closeObservabilityMenu}
      >
        {observabilityLinks.map(linkItem => (
          <MenuItem
            key={linkItem.label}
            component="a"
            href={linkItem.href}
            target="_blank"
            rel="noopener noreferrer"
            disabled={
              linkItem.disabled || !linkItem.href || linkItem.href === '#'
            }
            onClick={closeObservabilityMenu}
          >
            <ListItemIcon className={classes.menuItemIcon}>
              {linkItem.icon}
            </ListItemIcon>
            <ListItemText
              className={classes.menuItemText}
              primary={linkItem.label}
            />
          </MenuItem>
        ))}
      </Menu>
    </div>
  );

  const handleKubeconfigDownload = useCallback(() => {
    if (!kubeconfig) {
      return;
    }

    downloadTextFile(kubeconfig, kubeconfigFileName);
  }, [kubeconfig, kubeconfigFileName]);

  useEffect(() => {
    if (securityObjectIds.length === 0) {
      return;
    }

    getAdGroups(Array.from(new Set(securityObjectIds)));
  }, [getAdGroups, securityObjectIds]);

  let cardClass = '';
  if (variant === 'gridItem') {
    cardClass = classes.gridItemCard;
  } else if (variant === 'fullHeight') {
    cardClass = classes.fullHeightCard;
  }

  let cardContentClass = '';
  if (variant === 'gridItem') {
    cardContentClass = classes.gridItemCardContent;
  } else if (variant === 'fullHeight') {
    cardContentClass = classes.fullHeightCardContent;
  }

  const handleEdit = useCallback(async () => {
    if (!fetchedTenant) return;
    const formData = buildFormData(fetchedTenant, entity);
    navigateToTemplate({ formData, action: 'edit' });
  }, [entity, fetchedTenant, navigateToTemplate]);

  const handleDelete = useCallback(() => {
    const formData = { tenantFullName: entity.metadata.name };
    navigateToTemplate({ formData, action: 'delete' });
  }, [entity, navigateToTemplate]);

  const cardActions = hasAccess ? (
    <>
      <IconButton
        aria-label="Edit tenant"
        title={fetchedTenant ? 'Edit tenant' : 'Loading tenant data…'}
        disabled={!fetchedTenant}
        onClick={handleEdit}
      >
        <EditIcon />
      </IconButton>
      <IconButton
        aria-label="Delete tenant"
        title="Delete tenant"
        onClick={handleDelete}
      >
        <DeleteIcon />
      </IconButton>
    </>
  ) : null;

  return (
    <Card className={cardClass}>
      <CardHeader
        title={entity.metadata.name}
        subheader={subheader}
        action={cardActions}
      />
      <Divider />
      <CardContent className={`${cardContentClass} ${classes.cardContent}`}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Grid container spacing={1}>
              <AboutField
                label="Application"
                value={applicationName ?? 'Not configured'}
                gridSizes={{ xs: 12 }}
              >
                {fetchedTenant?.application ? (
                  <div className={classes.list}>
                    <Typography variant="body2">{applicationName}</Typography>
                    {applicationPU && (
                      <Typography variant="body2" color="textSecondary">
                        PU: {applicationPU}
                      </Typography>
                    )}
                    {applicationCC && (
                      <Typography variant="body2" color="textSecondary">
                        CC: {applicationCC}
                      </Typography>
                    )}
                  </div>
                ) : undefined}
              </AboutField>
              <AboutField
                label="Owner"
                value={ownerGroup?.title ?? owner}
                gridSizes={{ xs: 12 }}
              >
                {ownerGroup ? (
                  <EntityRefLink
                    entityRef={{
                      kind: ownerGroup.kind,
                      namespace: ownerGroup.namespace,
                      name: ownerGroup.name,
                    }}
                    title={ownerGroup.title}
                  />
                ) : undefined}
              </AboutField>
              <AboutField
                label="Security Groups"
                value="No security groups"
                gridSizes={{ xs: 12 }}
              >
                {renderSecurityGroupLinks(
                  securityObjectIds,
                  securityGroupNames,
                  classes.list,
                )}
              </AboutField>
              <AboutField
                label="PIM"
                value={pimStatus}
                gridSizes={{ xs: 12 }}
              />
              {tier === 'external' && (
                <AboutField
                  label="CAB: Inform clients"
                  value={cabInform ? 'Yes' : 'No'}
                  gridSizes={{ xs: 12 }}
                />
              )}
            </Grid>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Grid container spacing={1}>
              <AboutField
                label="Stage & Tier"
                value={`${capitalize(stage ?? '')} - ${capitalize(tier ?? '')}`}
                gridSizes={{ xs: 12 }}
              />
              <AboutField
                label="Cluster"
                value={`${clusterSubscription || 'Unknown'}/${
                  clusterName || clusterId || 'Unknown'
                }`}
                gridSizes={{ xs: 12 }}
              />
              <AboutField
                label="Dedicated Node Groups"
                value="No node groups"
                gridSizes={{ xs: 12 }}
              >
                {renderList(nodeGroups ?? [], classes.list)}
              </AboutField>
              <AboutField
                label="Namespace Quota"
                value={String(namespaceQuota ?? '')}
                gridSizes={{ xs: 12 }}
              />
              <AboutField label="Resource Quota" gridSizes={{ xs: 12 }}>
                <div className={classes.chipList}>
                  {cpuRequest && (
                    <Chip
                      variant="outlined"
                      size="small"
                      label={`CPU request: ${cpuRequest}`}
                    />
                  )}
                  {cpuLimit && (
                    <Chip
                      variant="outlined"
                      size="small"
                      label={`CPU limit: ${cpuLimit}`}
                    />
                  )}
                  {memoryRequest && (
                    <Chip
                      size="small"
                      variant="outlined"
                      label={`Memory request: ${memoryRequest}`}
                    />
                  )}
                  {memoryLimit && (
                    <Chip
                      size="small"
                      variant="outlined"
                      label={`Memory limit: ${memoryLimit}`}
                    />
                  )}
                </div>
              </AboutField>
            </Grid>
          </Grid>

          {argocdProject ? (
            <>
              <Grid item xs={12}>
                <Divider className={classes.sectionDivider} />
              </Grid>
              <Grid item xs={12}>
                <Grid container spacing={1}>
                  <AboutField
                    label="GitOps"
                    value="Not configured"
                    gridSizes={{ xs: 12 }}
                  >
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <div className={classes.list}>
                          {Boolean(rootProjectRepository?.length) && (
                            <>
                              <Typography variant="body2" color="textSecondary">
                                Root ArgoCD project repository
                              </Typography>
                              {renderRepositoryLinks(
                                rootProjectRepository!,
                                classes.list,
                                argocdProject.rootPath,
                              )}
                            </>
                          )}
                          {Boolean(additionalProjectRepositories?.length) && (
                            <>
                              <Typography variant="body2" color="textSecondary">
                                Additional repositories
                              </Typography>
                              {renderRepositoryLinks(
                                additionalProjectRepositories!,
                                classes.list,
                              )}
                            </>
                          )}
                        </div>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <div className={classes.list}>
                          {Boolean(helmRepositories?.length) && (
                            <>
                              <Typography variant="body2" color="textSecondary">
                                Helm repositories
                              </Typography>
                              {renderRepositoryLinks(
                                helmRepositories!,
                                classes.list,
                              )}
                            </>
                          )}
                        </div>
                      </Grid>
                    </Grid>
                  </AboutField>
                </Grid>
              </Grid>
              <Grid item xs={12}>
                <Divider className={classes.sectionDivider} />
              </Grid>
            </>
          ) : (
            <Grid item xs={12}>
              <Divider className={classes.sectionDivider} />
            </Grid>
          )}
          <AboutField label="Kubeconfig" gridSizes={{ xs: 12 }}>
            <div className={classes.actionBlock}>
              <div className={classes.actionBlockText}>
                <Typography variant="body2" className={classes.commandText}>
                  Download the kubeconfig file to use with kubectl.
                </Typography>
                <Typography
                  variant="body2"
                  color="textSecondary"
                  className={classes.commandText}
                >
                  kubectl get pods --kubeconfig {kubeconfigFileName}
                </Typography>
                {!kubeconfig && (
                  <Typography variant="body2" color="textSecondary">
                    No kubeconfig is available for this tenant.
                  </Typography>
                )}
              </div>
              <Button
                color="primary"
                variant="contained"
                startIcon={<GetAppIcon />}
                disabled={!kubeconfig}
                onClick={handleKubeconfigDownload}
              >
                Download
              </Button>
            </div>
          </AboutField>
        </Grid>
      </CardContent>
    </Card>
  );
};
